import { useState, useCallback } from 'react';
import { message } from 'antd';
import { KrybotService } from '../../../services/api/krybotService';
import { getApiErrorMessage } from '../../../utils/apiErrors';

/**
 * State and loaders for the agent SKILL file preview modal (one or more bundles).
 */
export function useAgentSkillModal(skillBundles: string[]) {
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const [skillLoading, setSkillLoading] = useState(false);
  const [selectedSkillBundle, setSelectedSkillBundle] = useState<string | null>(null);
  const [skillBody, setSkillBody] = useState<string | null>(null);
  const [skillPaths, setSkillPaths] = useState<string[]>([]);
  const [skillTruncated, setSkillTruncated] = useState(false);

  const loadSkillContent = useCallback(async (bundle: string) => {
    setSkillLoading(true);
    try {
      const res = await KrybotService.getSkillContent(bundle);
      setSkillBody(res.content);
      setSkillPaths(res.paths);
      setSkillTruncated(res.truncated);
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, 'Failed to load SKILL content'));
      throw err;
    } finally {
      setSkillLoading(false);
    }
  }, []);

  const openSkillModal = useCallback(async (bundlesOverride?: string[]) => {
    const bundles = bundlesOverride ?? skillBundles;
    if (bundles.length === 0) {
      message.warning('Set capabilities.skill_bundle or bridge_krybot_agent_id to load SKILL files.');
      return;
    }
    const initial = bundles[0];
    setSelectedSkillBundle(initial);
    setSkillBody(null);
    setSkillPaths([]);
    setSkillTruncated(false);
    setSkillModalOpen(true);
    try {
      await loadSkillContent(initial);
    } catch {
      setSkillModalOpen(false);
    }
  }, [skillBundles, loadSkillContent]);

  const handleSkillBundleSelect = useCallback(
    async (bundle: string) => {
      setSelectedSkillBundle(bundle);
      try {
        await loadSkillContent(bundle);
      } catch {
        /* message shown in loadSkillContent */
      }
    },
    [loadSkillContent]
  );

  return {
    skillModalOpen,
    setSkillModalOpen,
    skillLoading,
    selectedSkillBundle,
    skillBody,
    skillPaths,
    skillTruncated,
    openSkillModal,
    handleSkillBundleSelect,
  };
}
